import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import isEmpty from 'lodash/isEmpty';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
} from '@fortawesome/free-brands-svg-icons';

import env from 'Env';
import HitCardWishlistBtn from 'Components/Cards/HitCardWishlistBtn';
import PriceContainer from 'Components/Utility/Price/PriceContainer';
import store from 'Redux/store';
import HitLinkTo from 'Helpers/Product/HitLinkTo';
import hitToListItem from 'Components/Product/Seo/hit-list-item';
import 'Components/Cards/RecommendationsCard3Panel.scss';
import cleanDisplayName from 'Components/Product/Seo/CleanDisplayName';
import { ScriptTag } from 'Helpers/SEO/Tags';
import AddToCartBtn from 'Components/Cards/AddToCartBtn';
import Drm from 'Components/Utility/Drm';
import DlcBadge from 'Components/Cards/DlcBadge';
import ProductCover from 'Components/Utility/ProductCover';

const RecommendationsCard3Panel = ({
  hit,
  sizesOverride,
}) => {
  const {
    name,
    type,
    slug,
    cover,
    categories,
    price,
    display_type: displayType,
    display_name: displayName,
    pay_what_you_want: payWhatYouWant,
    free_to_play: freeToPlay,
    discount_percent: discountPercent,
    drm,
    operating_systems: operatingSystems,
    sdesc: description,
    sdescs: descriptions,
    discount_percent_override: discountPercentOverride,
    cover_type: coverType,
    portrait_cover: portraitCover,
    cover_alt_text: coverAltText,
  } = hit;

  const [isHovering, setIsHovering] = useState(false);
  const tagsContainerRef = useRef(null);
  const [visableTags, setVisableTags] = useState(categories);
  const OS = operatingSystems || [];

  let validDrm = '';
  let filteredDrm = [];
  if (hit && drm) filteredDrm = hit.drm.filter(d => d !== 'voucher' && d !== 'drm free');
  if (filteredDrm.length === 1) validDrm = filteredDrm[0];

  const onLinkClick = (link) => {
    store.dispatch({
      type: 'frontend/click/CLICK_INTERACTION',
      payload: { section: 'FanFavouriteCard', value: link },
    });
  };

  const productUrl = `${env.appProtocol}://${env.appUrl}/${window.locale || 'en'}/${type}/${slug}`;

  const schema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Review',
    '@id': `${productUrl}/reviews#${hit.review_id}`,
    reviewRating: {
      '@type': 'Rating',
      ratingValue: hit.rating,
      bestRating: 5,
      worstRating: 1,
    },
    author: {
      '@type': 'Person',
      name: cleanDisplayName(displayName),
    },
    reviewBody: hit.text,
    inLanguage: hit.locale,
    datePublished: hit.review_date,
    itemReviewed: hitToListItem(hit, 0).item,
  });

  useEffect(() => {
    const checkVisibility = () => {
      if (!tagsContainerRef || !tagsContainerRef.current) return;

      const container = tagsContainerRef.current;
      const tagElements = Array.from(container.children);
      const maxWidth = container.clientWidth;
      const GAP_WIDTH = 8; // Gap between tags

      const newVisibleTags = tagElements.reduce((acc, tagElement, index) => {
        // Add gap width for all elements except the first one
        const gapWidth = index > 0 ? GAP_WIDTH : 0;
        const currentWidth = acc.totalWidth + tagElement.offsetWidth + gapWidth;
        if (currentWidth <= maxWidth) {
          acc.totalWidth = currentWidth;
          acc.tags.push(tagElement.innerText);
        }
        return acc;
      }, { totalWidth: 0, tags: [] }).tags;

      setVisableTags(newVisibleTags);
    };

    const observer = new ResizeObserver(checkVisibility);
    if (tagsContainerRef && tagsContainerRef.current) observer.observe(tagsContainerRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <div className="fan-favourite-card-recommendations">
      {env.enhanceItemLists &&
        <ScriptTag id={`site-schema-${slug}`} type="application/ld+json" script={schema} />
      }
      <div className="content-container">
        <div
          className="left-container faux-block-link"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
        >
          <div className="card-actions-container d-none d-lg-flex">
            {isHovering &&
              <HitCardWishlistBtn
                productId={hit.product_id}
                slug={slug}
                hit={hit}
                componentType="FanFavouriteCard"
              />
            }
          </div>
          <Link
            to={HitLinkTo(hit)}
            className="w-100 faux-block-link__overlay-link"
            aria-label={`View details for ${name}`}
            onClick={() => onLinkClick(`/${type}/${slug}`)}
          >
            <div className="cover-container">
              <ProductCover
                cover={cover}
                portraitCover={portraitCover}
                alt={coverAltText || `Product cover for ${name}`}
                sizesOverride={sizesOverride}
                displayType={displayType}
                coverType={coverType}
              />
            </div>
          </Link>
          <div className="right-container faux-block-link">
            <div className="name-container">
              <Link to={HitLinkTo(hit)} className="faux-block-link__overlay-link game-name" onClick={() => onLinkClick(`/${type}/${slug}`)}>
                {name}
              </Link>
            </div>
            {!isEmpty(visableTags) &&
              <div className="tag-column-container" ref={tagsContainerRef}>
                {visableTags.map(tag =>
                  <div className="tag-column" key={tag}>
                    <div className="tag">
                      {tag}
                    </div>
                  </div>,
                )}
              </div>
            }
            <div className="review-text">
              {descriptions ? descriptions[(window.locale || 'en')] : description}
            </div>
            <div className="hitCardStripe__content">
              <div className="hitCardStripe__content__purchase">
                <div className="drm-container">
                  {validDrm &&
                    <Drm drmType={validDrm} />
                  }
                  <div className="drm-icons">
                    {OS && OS.includes('windows') &&
                      <FontAwesomeIcon icon={fabFaWindows} />
                    }
                    {OS && OS.includes('mac') &&
                      <FontAwesomeIcon icon={fabFaApple} />
                    }
                    {OS && OS.includes('linux') &&
                      <FontAwesomeIcon icon={fabFaLinux} />
                    }
                  </div>
                  {(type === 'dlc' || displayType === 'dlc') &&
                    <DlcBadge />
                  }
                </div>
                <div className="price-container">
                  {(hit.presale && freeToPlay) &&
                    <div className="hitCardStripe__content__purchase__price__text">
                      <FormattedMessage id="recommendations3card.availablesoon" defaultMessage="Available Soon" />
                    </div>
                  }

                  {(freeToPlay || hit.presale) &&
                    <div className="hitCardStripe__content__purchase__price__text">
                      <FormattedMessage id="recommendations3card.freetoplay" defaultMessage="Free to Play" />
                    </div>
                  }

                  {!hit.presale && !freeToPlay && !hit.hide_price &&
                    <PriceContainer
                      discountPercent={discountPercent}
                      discountPercentOverride={discountPercentOverride}
                      drm={validDrm}
                      fullPrice={hit.fullPrice}
                      hideDiscount={hit.hide_discount}
                      mystery={hit.mystery}
                      payWhatYouWant={payWhatYouWant}
                      price={price}
                      showAsterisk={!!hit.addVoucher}
                      slug={hit.slug}
                      tiered={hit.tiered}
                      type={hit.type}
                    />
                  }
                  <AddToCartBtn
                    newIcon
                    showInCartLink
                    hideButtonText
                    id={hit.product_id}
                    slug={hit.slug}
                    voucherToAdd={hit.addVoucher}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

RecommendationsCard3Panel.propTypes = {
  hit: PropTypes.shape({}),
  sizesOverride: PropTypes.string,
};

RecommendationsCard3Panel.defaultProps = {
  hit: {},
  sizesOverride: null,
};

export default RecommendationsCard3Panel;
